import { type RefObject, useEffect, type ReactNode } from 'react';
import clsx from 'clsx';
import { useGoalsBenefitsExperimentApi } from '@hungryroot/exp-152-goals-value-prop';
import {
  WasteLessFood,
  Food,
  SaveTime,
  TryNewThings,
  Bank,
} from '@hungryroot/icons';
import { Heading } from '@hungryroot/ramen';
import { Goals, type Quiz } from '../../defs/quiz';
import { ValuePropComponentTheme } from '../../defs/valueProp';
import { setSavedNavState } from '../../navigation/navigation';
import { ValuePropCard } from '../ValuePropCard/ValuePropCard';
import { ValuePropLayout } from '../ValuePropLayout/ValuePropLayout';
import styles from './GoalsBenefits.module.css';

type Props = {
  next: string | null;
  prev: string | null;
  quiz: Quiz;
  scrollRef: RefObject<HTMLDivElement>;
};

// Maps each goal to it's card in the defaultCardContentPriorityMap. Some goals share card content.
const DEFAULT_GOALS_PRIORITY_MAP = {
  [Goals.ImproveHealth]: 1,
  [Goals.LoseWeight]: 1,
  [Goals.SimplifyCooking]: 2,
  [Goals.SaveTime]: 2,
  [Goals.SaveMoney]: 3,
  [Goals.WasteLessFood]: 4,
  [Goals.GroceryShopLess]: 4,
  [Goals.TryNewThings]: 5,
};

// Keys correspond to the sort order for each of the cards.
function getDefaultCardContentPriorityMap(): {
  [key: number]: {
    text: ReactNode;
    icon: ReactNode;
  };
} {
  return {
    1: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          10,000+ easy recipes that{' '}
          <span className={styles['text-transparent']}>
            fit your needs, from high protein to gut-friendly to
            anti-inflammatory.
          </span>
        </Heading>
      ),
      icon: <Food className={styles.icon} />,
    },
    2: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          61% of our recipes are ready in under 15 minutes.{' '}
          <span className={styles['text-transparent']}>
            (We even have ready-to-eat options!)
          </span>
        </Heading>
      ),
      icon: <SaveTime className={styles.icon} />,
    },
    3: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          74% of customers save money{' '}
          <span className={styles['text-transparent']}>
            by ditching takeout, reducing food waste, and fewer trips to the
            store.
          </span>
        </Heading>
      ),
      icon: <Bank className={styles.icon} />,
    },
    4: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          Roughly 40% of food in the US goes to waste.{' '}
          <span className={styles['text-transparent']}>
            But with a personalized plan, your food gets put to tasty use.
          </span>
        </Heading>
      ),
      icon: <WasteLessFood className={styles.icon} />,
    },
    5: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          <span className={styles['text-transparent']}>With nearly</span> 1,000
          different foods to try,{' '}
          <span className={styles['text-transparent']}>
            you’re bound to discover — and fall in love with — tons of new eats.
          </span>
        </Heading>
      ),
      icon: <TryNewThings className={styles.icon} />,
    },
  };
}

export function GoalsBenefits({ next, prev, quiz, scrollRef }: Props) {
  const { goals: customerGoals } = quiz;
  const theme = ValuePropComponentTheme.Avocado;
  const defaultCardContentPriorityMap = getDefaultCardContentPriorityMap();

  const { cardContent, isVariant } =
    useGoalsBenefitsExperimentApi<Goals, typeof styles>(
      customerGoals,
      defaultCardContentPriorityMap,
      DEFAULT_GOALS_PRIORITY_MAP,
      styles
    ) || {};

  useEffect(() => {
    if (next !== null) {
      setSavedNavState(next);
    }
  }, [next]);

  if (cardContent === undefined) {
    return;
  }

  return (
    <ValuePropLayout
      headerText="Hungryroot makes it easy to achieve your goals"
      theme={theme}
      cards={cardContent.map(({ text, icon }, i) => (
        <ValuePropCard
          testid={`goals-value-prop-card-${i}`}
          key={i}
          text={text}
          icon={icon}
          theme={theme}
          className={clsx({ [styles['card-variant']]: isVariant })}
        />
      ))}
      next={next}
      prev={prev}
      scrollRef={scrollRef}
    />
  );
}
